import React, { useEffect, useState } from 'react'
import { useRecoilState, useResetRecoilState, useSetRecoilState } from 'recoil'
import { activeStepState, formDataState, loadingState } from '../helpers'
import { useNavigate, useLocation } from 'react-router-dom'
import Step3 from './step3.component'
import ActivationResult from './activationResult.component'
import CustomerScan from '../customer/scan.component'
import CustomerInfo from '../customer/info.component'
import SuccessError from '../layout/successerror.component'
import M1Stepper from '../layout/stepper.component'
import ScanSim from '../layout/scansim.component'
import simService from '../../services/sim.service'
import SessionTimeoutHandler from '../sessiontimeout.component'
import config from '../../config'
import CheckNActive from './checkNActive.component'
import useSnackbar from '../../data/use-snackbar'
import customerService from '../../services/customer.service'
import ConfigService from '../../services/config.service'
import { getErrorMessage } from '../../error-helper'

function getSteps() {
  return [
    {
      icon: 1,
      label: 'Scan SIM',
      title: 'Port-In & Registration',
      subTitle: 'Scan SIM Barcode',
    },
    {
      icon: 2,
      label: 'Register ID',
      title: 'Port-In & Registration',
      subTitle: 'Register ID',
    },
    {
      icon: 3,
      label: 'Upload Documents',
      title: 'Port-In & Registration',
      subTitle: '',
    },
    {
      icon: 4,
      label: 'Your Information',
      title: 'Port-In & Registration',
      subTitle: 'Verify Your Information',
    },
    {
      icon: 5,
      label: 'Review',
      title: 'Port-In & Registration',
      subTitle: 'Check & Register',
    },
    {
      icon: 6,
      label: 'SIM Activation',
      title: '',
      subTitle: '',
    },
    {
      icon: 7,
      label: 'Port-in Number',
      title: 'Port-In & Registration',
      subTitle: 'Input Port-in Number',
    },
  ]
}

function Portin() {
  const [activeStep, setActiveStep] = useRecoilState(activeStepState)
  const steps = getSteps()
  const resetData = useResetRecoilState(formDataState)
  const navigate = useNavigate()
  const location = useLocation()
  const setLoading = useSetRecoilState(loadingState)
  const [formData, setFormData] = useRecoilState(formDataState)
  const { enqueueSnackbar } = useSnackbar()
  const [skip, setSkip] = useState(false)

  useEffect(() => {
    const getEnv = async () => {
      const { disabledPortin } = await ConfigService.getEnv()
      if (disabledPortin) {
        navigate('/')
      }
    }

    try {
      getEnv()
    } catch (err) {
      console.log(err)
    }
  }, [navigate])

  const getStepContent = (step) => {
    switch (step) {
      default:
        return (
          <ScanSim step={getSteps()[parseInt(step)]} handleBack={handleBack} />
        )
      case 1:
        return (
          <CustomerScan
            step={getSteps()[parseInt(step)]}
            handleBack={handleBack}
          />
        )
      case 3:
        return (
          <CustomerInfo
            globalCheck={config.NODE_ENV !== 'development'}
            step={getSteps()[parseInt(step)]}
            handleBack={handleBack}
            handleCheckEligibility={checkEligibility}
          />
        )
      case 4:
        return (
          <CheckNActive
            step={getSteps()[parseInt(step)]}
            handleBack={handleBack}
            handleRegister={handleRegister}
          />
        )
      case 5:
        return <ActivationResult canRetry={true} handleRetry={handleRegister} />
      case 6:
        return (
          <Step3
            step={getSteps()[parseInt(step)]}
            handleBack={handleBack}
            handlePortin={handlePortin}
          />
        )
      case 7:
        return (
          <SuccessError
            canRetry={true}
            handleRetry={handlePortin}
            onRegisterAnother={handleRegisterAnother}
          />
        )
      case 8:
        return (
          <SuccessError
            canRetry={false}
            handleRetry={handlePortin}
            onRegisterAnother={handleRegisterAnother}
          />
        )
    }
  }

  const checkEligibility = async () => {
    let result = {
      valid: false,
      dno: '',
      errorMsg: '',
    }
    let count = 1

    const postResult = await customerService.eligibility(
      formData.idType,
      formData.idNo,
      formData.nationality,
      1,
      formData.portinMsisdn
    )
    if (postResult.Eligibility === 'YES') {
      result.valid = 'YES'
      result.dno = postResult?.Dno_Id
      return result
    }

    if (
      postResult.Eligibility === 'PENDING' ||
      postResult.Eligibility === 'NO'
    ) {
      do {
        await new Promise((res) => setTimeout(res, 5000))
        const getResult = await customerService.eligibilityResult(
          postResult.Transaction_Id,
          count === 3
        )
        if (getResult.Eligibility === 'YES' || getResult.Eligibility === 'NO') {
          result.valid = 'YES'
          result.dno = getResult?.Dno_Id
        } else if (getResult && getResult.Eligibility === 'FAIL') {
          result.valid = 'FAIL'
          result.errorMsg =
            "Unable to check the customer's eligibility at the moment. Please try again at a later time."
          break
        } else if (getResult && getResult.Eligibility === 'PENDING') {
          if (count === 3) {
            result.valid = 'PENDING'
            result.errorMsg =
              "Unable to check the customer's eligibility at the moment. Please try again at a later time."
            break
          } else {
            count++
          }
        } else {
          result.valid = 'NO'
          result.errorMsg =
            'Customer exceeds the maximum of 3 global prepaid lines. Please de-register one of the prepaid lines and try again.'
          break
        }
      } while (count <= 3 && !result.valid)
    } else if (postResult.Eligibility === 'FAIL') {
      result.valid = 'FAIL'
      result.errorMsg =
        "Unable to check the customer's eligibility at the moment. Please try again at a later time."
    } else {
      result.valid = 'NO'
      result.errorMsg =
        'Customer exceeds the maximum of 3 global prepaid lines. Please de-register one of the prepaid lines and try again.'
    }

    return result
  }

  const handleRegister = async (isRetry = false) => {
    setLoading(true)
    try {
      if (formData.orderId) {
        const order = await simService.order(formData.orderId)
        if (order && order.state.toUpperCase() === 'COMPLETED') {
          setFormData({
            ...formData,
            isOnboardSuccess: true,
          })
        } else {
          setFormData({
            ...formData,
            isOnboardSuccess: false,
            onboardErrorTitleType: 1,
            onboardErrorType: 1,
          })
        }
      } else {
        const activateRequest = {
          ...formData,
          isPortin: true,
        }
        const register = await simService.register(activateRequest)

        if (register && register.activated) {
          try {
            let count = 1,
              orderCompleted = false
            do {
              await new Promise((resolved) => setTimeout(resolved, 5000))
              const orderResult = await simService.order(register.orderId)
              if (
                orderResult &&
                orderResult.state.toUpperCase() === 'COMPLETED'
              ) {
                orderCompleted = true
              }

              count++
            } while (count <= 3 && !orderCompleted)

            if (orderCompleted) {
              setFormData({
                ...formData,
                isOnboardSuccess: true,
                orderId: register.orderId,
              })
              sessionStorage.clear()
            } else {
              setFormData({
                ...formData,
                isOnboardSuccess: false,
                onboardErrorTitleType: 0,
                onboardErrorType: 2,
                orderId: register.orderId,
              })
            }
          } catch (error) {
            setFormData({
              ...formData,
              isOnboardSuccess: false,
              onboardErrorTitleType: 0,
              onboardErrorType: 2,
              orderId: register.orderId,
            })
            setLoading(false)
            return setActiveStep((prevActiveStep) => prevActiveStep + 1)
          }
        }
      }
    } catch (error) {
      const errMsg = getErrorMessage(error)
      setFormData({
        ...formData,
        isOnboardSuccess: false,
        onboardErrorTitleType: 0,
        onboardErrorType:
          errMsg.indexOf('exceeds the maximum of 3') >= 0 ? 3 : 0,
      })
    } finally {
      setLoading(false)
      if (!isRetry) {
        setActiveStep((prevActiveStep) => prevActiveStep + 1)
      }
    }
  }

  const handlePortin = async () => {
    setLoading(true)
    try {
      const request = {
        msisdn: formData.msisdn,
        iccid: formData.iccid,
        imsi: formData.imsi,
        status: 'REGPORT',
      }

      const checkResult = await simService.check(request)

      if (checkResult.code <= 2000) {
        let result = await checkEligibility()
        if (result.valid === 'YES') {
          if (result.dno) {
            await portinSim(result.dno)
          } else {
            setLoading(false)
            return enqueueSnackbar('Invalid port-in number.')
          }
        } else if (result.valid === 'PENDING') {
          setFormData({
            ...formData,
            isSuccess: false,
            errorType: 4,
            errorTitleType: 3,
          })
          setLoading(false)
          setActiveStep((prevActiveStep) => prevActiveStep + 1)
        } else if (result.Eligibility === 'FAIL') {
          setFormData({
            ...formData,
            isSuccess: false,
            errorType: 4,
            errorTitleType: 3,
          })
          setLoading(false)
          setActiveStep((prevActiveStep) => prevActiveStep + 1)
        } else {
          setFormData({
            ...formData,
            isSuccess: false,
            errorType: 1,
            errorTitleType: 1,
          })
          setLoading(false)
          setActiveStep((prevActiveStep) => prevActiveStep + 1)
        }
      } else {
        setLoading(false)
        return enqueueSnackbar(
          'The activation of M1 temporary prepaid number in the progress. Please try again later.'
        )
      }
    } catch (error) {
      setFormData({
        ...formData,
        isSuccess: false,
        errorType: 2,
        errorTitleType: 3,
      })
      setLoading(false)
      setActiveStep((prevActiveStep) => prevActiveStep + 1)
    }
  }

  const portinSim = async (dno) => {
    try {
      const request = {
        name: formData.name,
        subType: formData.subType,
        nationality: formData.nationality,
        idType: formData.idType,
        idNo: formData.idNo,
        uen: formData.uen,
        msisdn: formData.msisdn,
        iccid: formData.iccid,
        imsi: formData.imsi,
        portinMsisdn: formData.portinMsisdn,
        dno: dno,
      }

      await simService.portin(request)

      setFormData({
        ...formData,
        isSuccess: true,
      })
      setLoading(false)
      setActiveStep((prevActiveStep) => prevActiveStep + 1)
    } catch (error) {
      setFormData({
        ...formData,
        isSuccess: false,
        errorType: 2,
        errorTitleType: 3,
      })
      setLoading(false)
      setActiveStep((prevActiveStep) => prevActiveStep + 1)
    }
  }

  const handleBack = () => {
    if (activeStep === 0) navigate('/')
    else if (activeStep === 3)
      setActiveStep((prevActiveStep) => prevActiveStep - 2)
    else setActiveStep((prevActiveStep) => prevActiveStep - 1)
  }

  const handleRegisterAnother = () => {
    resetData()
    navigate('/')
  }

  useEffect(() => {
    setActiveStep(0)

    return () => {
      setActiveStep(0)
    }
  }, [setActiveStep, resetData])

  useEffect(() => {
    if (activeStep === 0) {
      resetData()
    }
  }, [activeStep, resetData])

  useEffect(() => {
    if (location.state && location.state.code && !skip) {
      setActiveStep(1)
      setSkip(true)
    }
  }, [location.state, setActiveStep, skip])

  return (
    <>
      <SessionTimeoutHandler isSuccessErrorPage={activeStep > 5} />
      {activeStep < 8 && (
        <M1Stepper isStepper={true} activeStep={activeStep} steps={steps} />
      )}
      {getStepContent(parseInt(activeStep))}
    </>
  )
}

export default Portin
